<script setup lang="ts">
import { ref, computed, watch, onMounted, render, createVNode, defineAsyncComponent, nextTick } from 'vue'
import { api } from "../../../api/Api";
import { isEmpty } from '../../../utils/CommonFunctions';
import { Permission } from '../../../types/auth';
import { tokenManager } from '../../../api/utils/tokenManager';
import MessageBox from '../../../services/MessageBox';
import IconComponent from '../common/IconComponent.vue'

interface Group {
  name: string
  options: { id: string | number; name: string }[]
}

const props = defineProps<{
  modelValue: string | number | null
  groups?: Group[]
  optionLabel: string
  label: string
  formComponent?: string
  placeholder?: string
  error?: string | null
  endPoint?: string;
  readonly?: boolean;
  allowNewRecord?: boolean;
  extra?: string | number;
  extra1?: string | number;
  hasExtra?: boolean;
  remote?: boolean;
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', id: string | number): void
  (e: 'blur'): void
  (e: "select", option: any): void;
}>()

const groupList = ref<Group[]>([])
const isOpen = ref(false)
const searchQuery = ref('')
const selectedLabel = ref('')
const isLoading = ref(false);
const isHovered = ref(false);

const filteredGroups = computed(() => {
  if (!searchQuery.value) return groupList.value
  
  const query = searchQuery.value.toLowerCase()
  return groupList.value.map(group => ({
    name: group.name,
    options: group.options.filter(option => 
      option.name.toLowerCase().includes(query) ||
      group.name.toLowerCase().includes(query)
    )
  })).filter(group => group.options.length > 0)
})

const loadOptions = async (idValue: any = null) => {
  if (!props.endPoint) {
    groupList.value = props.groups || [];
    //if (props.modelValue) updateSelectedItemFrommodal(props.modelValue);
    return;
  }

  if (props.hasExtra && isEmpty(props.extra)) return;
  try {
    isLoading.value = true;
    const apiData = {
      extra: props.extra,
      extra1: props.extra1,
      search_query: searchQuery.value,
      id_value: idValue
    };
    const response = await api.post(
      "clients/" +
        props.endPoint +
        (props.endPoint.slice(-1) !== "/" ? "/" : "") +
        "list",
      apiData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        skipLoading: true,
      }
    );
    //fullItems.value = response.data;
    groupList.value = response.data.map((item: any) => ({
      id: item.id,
      name: item.name,
      options: item[props.optionLabel]
    }));
    updateSelectedItemFrommodal(props.modelValue);
  } catch (error) {
    console.error("Error loading options:", error);
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  if (!props.remote)
    loadOptions();
});

const handleInput = (e: Event) => {
  if (props.readonly) return;

  const input = e.target as HTMLInputElement;
  const value = input.value;

  if (value === "") {
    emit("update:modelValue", "");
    selectedLabel.value = "";
  }

  searchQuery.value = value;
  isOpen.value = true;

  if (props.remote && value.length > 3)
    debouncedLoadOptions();
};


// Debounce function
function debounce(func: Function, wait: number) {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return function (...args: any[]) {
    if (timeout !== null) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => func.apply(null, args), wait);
  };
}

// Debounce the loadOptions function
const debouncedLoadOptions = debounce(loadOptions, 300);

const updateSelectedItemFrommodal = (item_id: string | number | null) => {
  for (const group of groupList.value) {
    const option = group.options.find(opt => opt.id === item_id)
    if (option) {
      handleSelect(option);
      selectedLabel.value = option.name
      break
    }
  }
};

const showAddNew = computed(() => {
  console.log(filteredGroups.value);
  return (
    (props.allowNewRecord &&
      searchQuery.value &&
      filteredGroups.value.length === 0 &&
      !props.readonly) ||
    (props.allowNewRecord && filteredGroups.value.find(group => group.options.length === 0))
  );
});

const handleAddNew = async () => {
  try {
    // Dynamically import the component using the fixed path
    const comp_path = props.endPoint?.replace("/clients", "").replace(/-/g, "");
    console.log(comp_path);
    let comp =
      (comp_path?.slice(-1) == "/" ? "" : comp_path + "/") +
      props.label?.replace(/ /g, "") + (props.label?.slice(-1) == 's' ? "" : "s") +
      (props.formComponent ? props.formComponent : "") +
      "Form.vue";
    const modalComponent =
      "../../../features" + (comp.substring(0, 1) == "/" ? "" : "/") + comp;
    const FormModal = defineAsyncComponent(() =>
      import(/* @vite-ignore */ modalComponent).catch(() => {
        MessageBox.show("Could not load modal Form", "error");
        return null;
      })
    );

    console.log(modalComponent);

    // Create modal container
    const container = document.createElement("div");
    document.body.appendChild(container);

    const perm: Permission[] = tokenManager.getPermissions();
    const rpath = props.endPoint?.replace("clients/", "");
    const permissions = perm.filter(function (itm) {
      return itm.route_path == rpath;
    });
    // Create and mount modal
    const vnode = createVNode(FormModal, {
      //initialData: { name: searchQuery.value },
      permissions: permissions,
      title: props.label,
      endPoint: props.endPoint,
      mode: "create",
      listCreateName: searchQuery.value,
      onSave: async (newRecord: any) => {
        const newOption = {
          id: newRecord.id.toString(),
          name: newRecord.name,
        };
        loadOptions();

        // Select the new record
        handleSelect(newOption);

        // Remove modal
        render(null, container);
        container.remove();
      },
      onClose: () => {
        render(null, container);
        container.remove();
      },
    });

    // Render the modal
    render(vnode, container);
  } catch (error) {
    console.error("Error loading modal component:", error);
  }
};

const clearSelection = (e: MouseEvent) => {
  if (props.readonly) return;

  e.stopPropagation();
  emit("update:modelValue", "");
  emit("select", '');
  selectedLabel.value = "";
  searchQuery.value = "";
};

const handleSelect = (option: { id: string | number; name: string }) => {
  emit('update:modelValue', option.id)
  selectedLabel.value = option.name
  searchQuery.value = ''
  isOpen.value = false
}

const handleKeydown = (e: KeyboardEvent) => {
  if (props.readonly) return;

  if (e.key === "Backspace" && !searchQuery.value && selectedLabel.value) {
    e.preventDefault();
    emit("update:modelValue", "");
    selectedLabel.value = "";
    isOpen.value = true;
  }
};

const handleBlur = () => {
  setTimeout(() => {
    //isOpen.value = false
    emit('blur')
  }, 200)
}

// Find and set the selected label when value changes
watch(() => props.modelValue, (newValue) => {
  if (props.remote && props.readonly && newValue) {
      loadOptions(newValue)
  }
  updateSelectedItemFrommodal(newValue)
}, { immediate: true })

watch(
  () => props.extra,
  () => {
    if (!props.readonly) {
      console.log(' props.extra changed, clearing selection');
      groupList.value = []
      emit("update:modelValue", "");
      emit("select", '');
      selectedLabel.value = "";
      searchQuery.value = "";
    }
    if (props.extra && !props.remote) {
      loadOptions();
    }
  },
  { deep: true }
);


const searchGroupedSelectInput = ref<HTMLInputElement | null>(null);
const calculateDropdownDirection = () => {
  const dropdownMaxHeight = 15 * 16; // 15rem in pixels

  if (!searchGroupedSelectInput.value) return;

  const inputRect = searchGroupedSelectInput.value.getBoundingClientRect();
  const spaceBelow = window.innerHeight - inputRect.bottom;
  const spaceAbove = inputRect.top;

  if (spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow) {
    dropdownDirection.value = "up";
  } else {
    dropdownDirection.value = "down";
  }
};
const dropdownDirection = ref("down"); 

watch(isOpen, (newValue) => {
  if (newValue) {
    nextTick(() => {
      calculateDropdownDirection();
    });
  }
});

</script>

<template>
  <div class="relative">
    <label v-if="label" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
      {{ label }}
    </label>
    
    <div class="relative"
    @mouseenter="isHovered = true"
      @mouseleave="isHovered = false">
      <input
        type="text"
        
        :value="selectedLabel || searchQuery"
        ref="searchGroupedSelectInput"
        @input="handleInput"
        @keydown="handleKeydown"
        @focus="isOpen = true"
        :readonly="readonly"
        @blur="handleBlur"
        :placeholder="isLoading ? 'Loading ... ' : (!isEmpty(placeholder) ? placeholder : label) + (remote == true ? ' (type to load list)' : '')"
        class="w-full px-3 py-2 rounded-lg border text-gray-900 dark:text-white bg-white dark:bg-gray-900"
        :class="[
          error
            ? 'border-red-300 dark:border-red-700'
            : 'border-gray-200 dark:border-gray-700'
        ]"
      />

      <button
        v-if="selectedLabel && isHovered && !readonly"
        @click="clearSelection"
        @mousedown.prevent
        class="absolute right-8 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
      >
        <IconComponent name="Trash2" />
      </button>
      
      <span 
        class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
      >
        ▼
      </span>
    </div>

    <!-- Error message -->
    <p v-if="error" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ error }}</p>

    <!-- Dropdown -->
    <div
      ref="drop-downdiv"
      v-if="isOpen && !readonly"
      class="z-[3000] max-w-[300px] w-full mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-h-60 overflow-y-auto"
      :class="{
        'mt-1': dropdownDirection === 'down',
        'bottom-0 mb-1': dropdownDirection === 'up',
      }"
    >
      <div v-if="filteredGroups.length === 0" class="p-4 text-center text-gray-500 dark:text-gray-400">
        No results found
      </div>
      
      <div v-for="group in filteredGroups" :key="group.name">
        <!-- Group Header -->
        <div class="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900">
          {{ group.name }}
        </div>
        
        <!-- Group Options -->
        <button
          v-for="option in group.options"
          :key="option.id"
          @click="handleSelect(option)"
          class="pl-10 w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700"
          :class="[
            option.id === modelValue
              ? 'bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400'
              : 'text-gray-900 dark:text-white'
          ]"
        >
          {{ option.name }}
        </button>
      </div>
      <button
          v-if="showAddNew"
          @click="handleAddNew"
          class="w-full px-4 py-2 text-left text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/50 flex items-center gap-2"
        >
          <IconComponent name="Plus" />
          Add
          {{
            searchQuery.length > 0
              ? '"' + searchQuery + '""'
              : "new " + props.label
          }}
        </button>
    </div>
  </div>
</template>